import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { CircleAlert, CircleCheck } from 'lucide-react';

interface ToastCtx {
  avisar: (mensaje: string, tipo?: 'ok' | 'error') => void;
}

const Ctx = createContext<ToastCtx | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [t, setT] = useState<{ mensaje: string; tipo: 'ok' | 'error' } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const avisar = useCallback((mensaje: string, tipo: 'ok' | 'error' = 'ok') => {
    setT({ mensaje, tipo });
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setT(null), 3500);
  }, []);

  useEffect(() => () => clearTimeout(timer.current), []);
  const valor = useMemo(() => ({ avisar }), [avisar]);

  return (
    <Ctx.Provider value={valor}>
      {children}
      {t && (
        <div role="status" className={`toast ${t.tipo === 'error' ? 'error' : ''}`}>
          {t.tipo === 'error' ? <CircleAlert aria-hidden /> : <CircleCheck aria-hidden />}
          {t.mensaje}
        </div>
      )}
    </Ctx.Provider>
  );
}

export function useToast(): ToastCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useToast fuera de ToastProvider');
  return c;
}
